import React, { useState, useEffect } from 'react';
import Icon from '../../../components/appIcon';
import Button from '../../../components/ui/button';
import Input from '../../../components/ui/input';
import axiosInstance from 'utils/axiosInstance';
import { useAuth } from 'components/ui/navigationGuard';

const AttendanceHistory = ({ attendanceRecords }) => {
  const { user, isAuthenticated } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [records, setRecords] = useState(attendanceRecords || []);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({});
  const recordsPerPage = 10;

  useEffect(() => {
    setRecords(attendanceRecords || []);
  }, [attendanceRecords]);

  const fetchAttendanceHistory = async (page = 1, search = '', status = 'all') => {
    try {
      setLoading(true);
      const employee_id = user.employee_id;
      const response = await axiosInstance.get(`/employee/me/attendance-history/${employee_id}`, {
        params: { 
          page, 
          limit: recordsPerPage,
          search,
          status 
        }
      });
      
      setRecords(response?.data?.data?.attendanceRecords || []);
      setPagination(response?.data?.data?.pagination || {});
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching attendance history:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendanceHistory(currentPage, searchTerm, statusFilter);
  }, [currentPage, searchTerm, statusFilter]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'present':
        return 'text-success bg-success/10 border-success/20';
      case 'late':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'absent':
        return 'text-error bg-error/10 border-error/20';
      case 'early_departure':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present':
        return 'CheckCircle';
      case 'late':
        return 'Clock';
      case 'absent':
        return 'XCircle';
      case 'early_departure':
        return 'ArrowLeft';
      default:
        return 'Circle';
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatWeekday = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { weekday: 'long' });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '--:--';
    const time = new Date(timeString);
    return time?.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg enterprise-shadow-card">
      <div className="p-6 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-lg font-semibold text-foreground">Attendance History</h2>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              type="search"
              placeholder="Search records..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full sm:w-64"
            />
            
            <select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className="px-3 py-2 border border-border rounded-md bg-input text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="all">All Status</option>
              <option value="present">Present</option>
              <option value="late">Late</option>
              <option value="absent">Absent</option>
              <option value="early_departure">Early Leave</option>
            </select>
          </div>
        </div>
      </div>
      
      {loading ? (
        <div className="p-8 text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-sm text-muted-foreground mt-2">Loading attendance records...</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Date</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Check In</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Check Out</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Hours</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Location</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Notes</th>
                </tr>
              </thead>
              <tbody>
                {records?.map((record, index) => (
                  <tr key={record?.id} className={`border-b border-border ${index % 2 === 0 ? 'bg-background' : 'bg-muted/20'}`}>
                    <td className="p-4">
                      <div className="text-sm font-medium text-foreground">
                        {formatDate(record?.date)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatWeekday(record?.date)}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className={`inline-flex items-center space-x-2 px-2 py-1 rounded-full border text-xs font-medium ${getStatusColor(record?.status)}`}>
                        <Icon name={getStatusIcon(record?.status)} size={12} />
                        <span className="capitalize">{record?.status?.replace('_', ' ')}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-foreground">
                        {formatTime(record?.checkIn)}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-foreground">
                        {formatTime(record?.checkOut)}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-sm font-medium text-foreground">
                        {record?.totalHours || '0:00'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-1 text-sm text-muted-foreground">
                        {record?.location && (
                          <>
                            <Icon name="MapPin" size={12} />
                            <span className="truncate max-w-[100px]">{record?.location}</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      {record?.notes && (
                        <div className="flex items-center space-x-1">
                          <Icon name="MessageSquare" size={12} className="text-muted-foreground" />
                          <span className="text-xs text-muted-foreground truncate max-w-[120px]" title={record?.notes}>
                            {record?.notes}
                          </span>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {records?.length === 0 && (
            <div className="p-8 text-center">
              <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No records found</h3>
              <p className="text-muted-foreground">
                {searchTerm || statusFilter !== 'all' ? 'Try adjusting your search or filter criteria.' : 'No attendance records available.'}
              </p>
            </div>
          )}
          
          {/* Pagination */}
          {pagination.pages && pagination.pages > 1 && (
            <div className="p-4 border-t border-border">
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Showing {(currentPage - 1) * recordsPerPage + 1} to {Math.min(currentPage * recordsPerPage, pagination.total)} of {pagination.total} records
                </div>
                
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    iconName="ChevronLeft"
                    iconSize={16}
                  >
                    Previous
                  </Button>
                  
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <Button
                          key={page}
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePageChange(page)}
                          className="w-8 h-8"
                        >
                          {page}
                        </Button>
                      );
                    })}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === pagination.pages}
                    iconName="ChevronRight"
                    iconSize={16}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AttendanceHistory;